import type {
  Activity,
  AuditEvent,
  Contact,
  CrmAttachment,
  DashboardData,
  DedupeCandidate,
  FollowUp,
  Opportunity,
  Order,
  Quote,
  SampleRequest,
  TeamMember,
  UserProfile,
  Vendor,
  VendorDetail,
} from "../types";

export const demoOrganizationId = "10000000-0000-4000-8000-000000000001";
export const demoAdamUserId = "20000000-0000-4000-8000-000000000001";
const organizationId = demoOrganizationId;
const techUserId = demoAdamUserId;
const georgiesVendorId = "30000000-0000-4000-8000-000000000001";
const northsideVendorId = "30000000-0000-4000-8000-000000000002";
const merriVendorId = "30000000-0000-4000-8000-000000000003";

export const demoAdamProfile: UserProfile = {
  user_id: techUserId,
  display_name: "Adam",
};

export const demoTeamMembers: TeamMember[] = [{ ...demoAdamProfile, role: "admin" }];

export const demoVendors: Vendor[] = [
  {
    id: georgiesVendorId,
    organization_id: organizationId,
    trading_name: "Georgie's Grocer",
    legal_name: "Georgie's Grocer Pty Ltd",
    abn: "51824753556",
    location_name: "South Melbourne Market",
    email: "orders@georgies.example",
    phone: "03 9000 1122",
    address_line_1: "322-326 Coventry Street",
    address_line_2: null,
    suburb: "South Melbourne",
    state: "VIC",
    postcode: "3205",
    status: "active",
    tags: ["market", "grocer"],
    notes: "Interested in small-batch pantry products.",
    last_activity_at: "2026-08-18T05:30:00.000Z",
    latitude: -37.8324,
    longitude: 144.9567,
    entity_type: "venue",
    lifecycle_stage: "customer",
    account_priority: "key",
    contact_cadence_days: 14,
    next_contact_at: "2026-08-25T23:00:00.000Z",
    owner_id: techUserId,
    row_version: 1,
    created_at: "2026-08-01T01:00:00.000Z",
    updated_at: "2026-08-18T05:30:00.000Z",
  },
  {
    id: northsideVendorId,
    organization_id: organizationId,
    trading_name: "Northside Provisions",
    legal_name: null,
    abn: "53004085616",
    location_name: "Preston Market",
    email: "hello@northside.example",
    phone: "03 9000 2200",
    address_line_1: "30 The Centreway",
    address_line_2: null,
    suburb: "Preston",
    state: "VIC",
    postcode: "3072",
    status: "prospect",
    tags: ["deli"],
    notes: null,
    last_activity_at: "2026-08-16T03:15:00.000Z",
    latitude: -37.7388,
    longitude: 145.0024,
    entity_type: "venue",
    lifecycle_stage: "qualified",
    account_priority: "high",
    contact_cadence_days: 7,
    next_contact_at: "2026-08-20T00:00:00.000Z",
    owner_id: techUserId,
    row_version: 1,
    created_at: "2026-08-05T01:00:00.000Z",
    updated_at: "2026-08-16T03:15:00.000Z",
  },
  {
    id: merriVendorId,
    organization_id: organizationId,
    trading_name: "Merri Pantry",
    legal_name: null,
    abn: null,
    location_name: "Northcote",
    email: null,
    phone: "0412 555 019",
    address_line_1: "88 High Street",
    address_line_2: null,
    suburb: "Northcote",
    state: "VIC",
    postcode: "3070",
    status: "active",
    tags: ["pantry", "gift hampers"],
    notes: null,
    last_activity_at: "2026-08-12T06:00:00.000Z",
    latitude: -37.7699,
    longitude: 144.9984,
    entity_type: "venue",
    lifecycle_stage: "customer",
    account_priority: "normal",
    contact_cadence_days: 21,
    next_contact_at: null,
    owner_id: techUserId,
    row_version: 1,
    created_at: "2026-07-24T01:00:00.000Z",
    updated_at: "2026-08-12T06:00:00.000Z",
  },
];

export const demoActivities: Activity[] = [
  {
    id: "40000000-0000-4000-8000-000000000001",
    vendor_id: georgiesVendorId,
    activity_type: "order",
    occurred_at: "2026-08-18T05:30:00.000Z",
    summary: "One case of salted duck yolk chilli crisp ordered",
    details: "Syrup declined. Revisit the chilli crisp range after first sell-through.",
    source: "voice",
    raw_transcript: "They took one case of the crisp and passed on the syrup.",
    outcome: "First wholesale order placed",
    next_action: "Check sell-through next week",
    channel: "in_person",
    direction: "two_way",
    created_by: techUserId,
    author: demoAdamProfile,
    created_at: "2026-08-18T05:31:00.000Z",
    row_version: 1,
  },
  {
    id: "40000000-0000-4000-8000-000000000002",
    vendor_id: northsideVendorId,
    activity_type: "visit",
    occurred_at: "2026-08-16T03:15:00.000Z",
    summary: "Samples left with the grocery buyer",
    details: "Buyer asked for wholesale pricing by email.",
    source: "manual",
    raw_transcript: null,
    channel: "in_person",
    direction: "two_way",
    created_by: techUserId,
    author: demoAdamProfile,
    created_at: "2026-08-16T03:16:00.000Z",
    row_version: 1,
  },
];

export const demoFollowUps: FollowUp[] = [
  {
    id: "50000000-0000-4000-8000-000000000001",
    vendor_id: georgiesVendorId,
    due_at: "2026-08-25T23:00:00.000Z",
    action: "Check chilli crisp sell-through and restock",
    status: "open",
    priority: "high",
    assigned_to: techUserId,
    snoozed_until: null,
    snooze_count: 0,
    row_version: 1,
    created_at: "2026-08-18T05:31:00.000Z",
  },
  {
    id: "50000000-0000-4000-8000-000000000002",
    vendor_id: northsideVendorId,
    due_at: "2026-08-20T00:00:00.000Z",
    action: "Email wholesale pricing",
    status: "open",
    priority: "normal",
    assigned_to: techUserId,
    snoozed_until: null,
    snooze_count: 0,
    row_version: 1,
    created_at: "2026-08-16T03:16:00.000Z",
  },
];

export const demoOrders: Order[] = [
  {
    id: "60000000-0000-4000-8000-000000000001",
    vendor_id: georgiesVendorId,
    activity_id: "40000000-0000-4000-8000-000000000001",
    order_reference: "JK-1042",
    description: "1 case salted duck yolk chilli crisp",
    status: "pending",
    commitment: "confirmed",
    quantity: 1,
    unit: "case",
    amount: 168,
    expected_at: "2026-08-21T00:00:00.000Z",
    created_at: "2026-08-18T05:31:00.000Z",
    row_version: 1,
  },
];

export const demoOpportunities: Opportunity[] = [
  {
    id: "81000000-0000-4000-8000-000000000001",
    organization_id: organizationId,
    vendor_id: northsideVendorId,
    title: "Initial pantry range",
    stage: "sample",
    estimated_value: 1200,
    currency: "AUD",
    probability: 35,
    expected_close_date: "2026-09-15",
    next_step: "Send wholesale pricing",
    owner_id: techUserId,
    closed_at: null,
    lost_reason: null,
    created_by: techUserId,
    updated_by: techUserId,
    row_version: 1,
    created_at: "2026-08-16T03:16:00.000Z",
    updated_at: "2026-08-16T03:16:00.000Z",
  },
];

export const demoSampleRequests: SampleRequest[] = [
  {
    id: "82000000-0000-4000-8000-000000000001",
    organization_id: organizationId,
    vendor_id: northsideVendorId,
    opportunity_id: demoOpportunities[0]?.id ?? null,
    product_id: null,
    description: "Chilli crisp and syrup tasting set",
    quantity: 1,
    unit: "set",
    status: "delivered",
    sent_at: "2026-08-16T03:16:00.000Z",
    delivered_at: "2026-08-16T03:16:00.000Z",
    feedback: null,
    owner_id: techUserId,
    created_by: techUserId,
    updated_by: techUserId,
    row_version: 1,
    created_at: "2026-08-16T03:16:00.000Z",
    updated_at: "2026-08-16T03:16:00.000Z",
  },
];

export const demoQuotes: Quote[] = [];
export const demoAttachments: CrmAttachment[] = [];
export const demoDedupeCandidates: DedupeCandidate[] = [];

export const demoContacts: Contact[] = [
  {
    id: "70000000-0000-4000-8000-000000000001",
    vendor_id: georgiesVendorId,
    name: "Georgia Bell",
    title: "Owner",
    email: "georgia@georgies.example",
    phone: "0412 555 010",
    priority: 1,
  },
  {
    id: "70000000-0000-4000-8000-000000000002",
    vendor_id: georgiesVendorId,
    name: "Alex Reed",
    title: "Buyer",
    email: null,
    phone: null,
    priority: 2,
  },
];

export const demoAuditEvents: AuditEvent[] = [
  {
    id: 3,
    organization_id: organizationId,
    table_name: "vendors",
    record_id: georgiesVendorId,
    operation: "UPDATE",
    actor_id: techUserId,
    actor: demoAdamProfile,
    changed_at: "2026-08-18T05:32:00.000Z",
    old_data: {
      id: georgiesVendorId,
      trading_name: "Georgie's Grocer",
      status: "prospect",
    },
    new_data: {
      id: georgiesVendorId,
      trading_name: "Georgie's Grocer",
      status: "active",
    },
  },
  {
    id: 2,
    organization_id: organizationId,
    table_name: "activities",
    record_id: demoActivities[0]?.id ?? "",
    operation: "INSERT",
    actor_id: techUserId,
    actor: demoAdamProfile,
    changed_at: "2026-08-18T05:31:00.000Z",
    old_data: null,
    new_data: {
      id: demoActivities[0]?.id,
      summary: demoActivities[0]?.summary,
      activity_type: demoActivities[0]?.activity_type,
    },
  },
  {
    id: 1,
    organization_id: organizationId,
    table_name: "contacts",
    record_id: demoContacts[0]?.id ?? "",
    operation: "INSERT",
    actor_id: techUserId,
    actor: demoAdamProfile,
    changed_at: "2026-08-01T01:05:00.000Z",
    old_data: null,
    new_data: {
      id: demoContacts[0]?.id,
      name: demoContacts[0]?.name,
      title: demoContacts[0]?.title,
    },
  },
];

export function makeDemoDashboard(): DashboardData {
  return {
    vendorCount: demoVendors.length,
    recentActivities: [...demoActivities]
      .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at))
      .map((activity) => ({
        ...activity,
        order_id: demoOrders.find((order) => order.activity_id === activity.id)?.id ?? null,
        vendor: {
          id: activity.vendor_id,
          trading_name:
            demoVendors.find((vendor) => vendor.id === activity.vendor_id)?.trading_name ??
            "Account",
        },
      })),
    dueFollowUps: demoFollowUps
      .filter((followUp) => followUp.status === "open")
      .sort((a, b) => a.due_at.localeCompare(b.due_at))
      .map((followUp) => ({ ...followUp, vendor: vendorSummary(followUp.vendor_id) })),
    pendingOrders: demoOrders
      .filter((order) => order.status === "pending" || order.status === "confirmed")
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .map((order) => ({ ...order, vendor: vendorSummary(order.vendor_id) })),
  };
}

export function makeDemoVendorDetail(id: string): VendorDetail | null {
  const vendor = demoVendors.find((item) => item.id === id);
  if (!vendor) return null;

  return {
    vendor,
    contacts: demoContacts.filter((contact) => contact.vendor_id === id),
    activities: demoActivities
      .filter((item) => item.vendor_id === id)
      .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)),
    orders: demoOrders
      .filter((item) => item.vendor_id === id)
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    followUps: demoFollowUps
      .filter((item) => item.vendor_id === id)
      .sort((a, b) => b.due_at.localeCompare(a.due_at)),
    opportunities: demoOpportunities.filter((item) => item.vendor_id === id),
    sampleRequests: demoSampleRequests.filter((item) => item.vendor_id === id),
    quotes: demoQuotes.filter((item) => item.vendor_id === id),
    attachments: demoAttachments,
    dedupeCandidates: demoDedupeCandidates,
    teamMembers: demoTeamMembers,
  };
}

function vendorSummary(vendorId: string) {
  return {
    id: vendorId,
    trading_name: demoVendors.find((vendor) => vendor.id === vendorId)?.trading_name ?? "Vendor",
  };
}
